import React from "react";
import dayjs from "dayjs";
import * as Router from "next/router";
import styled from "styled-components";

import * as u from "../utils";
import * as s from "../utils/stream";
import * as gtag from "../utils/gtag";
import f from "../firebase.client";
import Button from "./Button";
import Dialog from "./Dialog";
import * as I from "./Icons";
import * as ConnectionContext from "./Context.Connections";
import * as UserContext from "./UserContext";
import ConnectionType from "../lib/Domain/Types/ConnectionType";

export default function ConnectButton(props) {
  const connections = React.useContext(ConnectionContext.Context);
  const user = React.useContext(UserContext.Context);

  const router = Router.useRouter();
  const [isLoading, setIsLoading] = React.useState(false);
  const [showRemoveConnectionDialog, setShowRemoveConnectionDialog] =
    React.useState(false);

  const otherUserId = props.id;
  const connection = connections?.connIds[otherUserId];

  const isAcceptedConnection = connection?.state === "ACCEPTED";
  const isMyRequest =
    connection?.state === "PENDING" && connection?.isMyRequest;
  const isTheirRequest =
    connection?.state === "PENDING" && !connection?.isMyRequest;
  const isCancelled = connection?.state === "CANCELLED";
  const isIgnored =
    connection?.state === "PENDING" && connection?.substate === "IGNORED";
  const isRemoved = connection?.state === "REMOVED";
  const isPaid = !!user.membership?.id;

  if (props.id === user.id) return null;

  if (!connection && !isPaid && !isTheirRequest)
    return (
      <Button
        onClick={() => router.push("/digital/select-plan")}
        isLoading={isLoading}
        size={props.size}
        isWide
        data-cy="connect-button"
      >
        Upgrade to Connect
        <I.Lock />
      </Button>
    );

  if (!connection)
    return (
      <Button
        onClick={onConnect}
        isLoading={isLoading}
        size={props.size}
        text="Connect"
        isWide
        data-cy="connect-button"
      />
    );

  if (isCancelled || isIgnored)
    return (
      <Button
        onClick={onConnect}
        isLoading={isLoading}
        size={props.size}
        text="Connect"
        isWide
        data-cy="connect-button"
      />
    );

  if (isRemoved)
    return (
      <Button
        onClick={onConnect}
        isLoading={isLoading}
        size={props.size}
        text="Reconnect"
        isWide
      />
    );

  if (isAcceptedConnection) {
    return props.canRemoveConnection ? (
      <S.Buttons>
        <Button
          onClick={goMessage}
          size={props.size}
          text="Message"
          data-cy={`message-connection-button--${connection?.id}`}
          isWide
        />
        <Button
          isSecondary
          onClick={() => setShowRemoveConnectionDialog(true)}
          text="Remove"
          data-cy={`remove-connection-button--${connection?.id}`}
          isWide
        />
        {showRemoveConnectionDialog && (
          <RemoveConnectionDialog
            nameOfOtherUser={props.name}
            onConfirm={onConfirmRemoveConnection}
            onDialogClose={() => {
              setShowRemoveConnectionDialog(false);
              connections.refresh();
            }}
          />
        )}
      </S.Buttons>
    ) : (
      <Button
        isSecondary
        onClick={goMessage}
        size={props.size}
        text="Message"
        data-cy={`message-connection-button--${connection?.id}`}
        isWide
      />
    );
  }

  if (isMyRequest) {
    return (
      <Button
        isSecondary
        size={props.size}
        onClick={() => onRemoveRequest()}
        data-cy={`remove-request-button--${connection?.id}`}
        text="Remove request"
        isWide
      />
    );
  }

  if (isTheirRequest) {
    return (
      <S.Buttons>
        <Button
          isCircle={props.isShort}
          isSecondary
          size={props.isShort ? "big" : props.size}
          onClick={onReject}
          data-cy={`reject-connection-button--${connection?.id}`}
        >
          {props.isShort ? <I.Cross /> : "Reject"}
        </Button>
        <Button
          isCircle={props.isShort}
          onClick={onAccept}
          size={props.isShort ? "big" : props.size}
          data-cy={`accept-connection-button--${connection?.id}`}
        >
          {props.isShort ? <I.TickForButton /> : "Accept"}
        </Button>
      </S.Buttons>
    );
  }

  return null;

  async function onConnect(e) {
    e.stopPropagation();

    setIsLoading(true);

    const payload = {
      receiverId: otherUserId,
      requesterId: user.id,
      state: "PENDING",
      type: router.query.qrcode
        ? ConnectionType.QR_CODE_WEB
        : ConnectionType.WEB,
    };

    if (!connection) {
      payload.id = u.getRandomStr(28);
      payload.createdAt = dayjs().unix();

      await f
        .firestore()
        .collection("connections")
        .doc(payload.id)
        .set(payload);
      const newConnections = {
        [payload.receiverId]: {
          state: "PENDING",
          id: payload.id,
          isMyRequest: true,
        },
        ...connections.connIds,
      };
      connections.setConnIds(newConnections);
    } else {
      await f
        .firestore()
        .collection("connections")
        .doc(connection.id)
        .set(payload, { merge: true });
      const newConnections = { ...connections.connIds };
      newConnections[otherUserId] = {
        id: connection.id,
        state: "PENDING",
        isMyRequest: true,
      };
      connections.setConnIds(newConnections);
    }

    try {
      const request = {
        verb: "connectionRequest",
        object: {},
      };
      const client = await s.connect();
      const connections = client.feed("notification", otherUserId);
      await connections.addActivity(request);
    } catch {
      console.error("failed to notify");
    }

    setIsLoading(false);

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Add User",
      value: router.route,
    });
  }

  async function onAccept(event) {
    event.stopPropagation();
    setIsLoading(true);
    const { id, isMyRequest } = connections.connIds[otherUserId];
    await f
      .firestore()
      .collection("connections")
      .doc(id)
      .set({ state: "ACCEPTED" }, { merge: true });
    const newConnections = {
      ...connections.connIds,
      [otherUserId]: { state: "ACCEPTED", id, isMyRequest },
    };
    connections.setConnIds(newConnections);
    setIsLoading(false);

    try {
      const request = {
        verb: "connectionAccept",
        object: {},
      };
      const client = await s.connect();
      const connections = client.feed("notification", otherUserId);
      await connections.addActivity(request);
    } catch {
      console.error("failed to notify");
    }

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Accept User",
      value: router.route,
    });
  }

  async function onReject() {
    setIsLoading(true);
    const { id } = connections.connIds[otherUserId];
    await f
      .firestore()
      .collection("connections")
      .doc(id)
      .set({ state: "REJECTED" }, { merge: true });
    connections.refresh();
    setIsLoading(false);

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Reject User",
      value: router.route,
    });
  }

  async function onRemoveRequest() {
    setIsLoading(true);
    const { id } = connections.connIds[otherUserId];
    await f
      .firestore()
      .collection("connections")
      .doc(id)
      .set({ state: "CANCELLED" }, { merge: true });
    const newConnections = {
      ...connections.connIds,
      [otherUserId]: {
        ...connections.connIds[otherUserId],
        substate: "IGNORED",
      },
    };
    connections.setConnIds(newConnections);
    setIsLoading(false);

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Cancel Add User",
      value: router.route,
    });
  }

  function goMessage(e) {
    e.stopPropagation();
    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Message User",
      value: router.route,
    });

    router.push(`/messages?id=${otherUserId}`);
  }

  async function onConfirmRemoveConnection() {
    setIsLoading(true);
    const { id } = connections.connIds[props.id];
    await f
      .firestore()
      .collection("connections")
      .doc(id)
      .set({ state: "REMOVED" }, { merge: true });
    setIsLoading(false);

    gtag.event({
      name: gtag.GA_EVENT.CONNECT_ENGAGEMENT,
      category: "Connection",
      label: "Remove User Connection",
      value: router.route,
    });
  }
}

function RemoveConnectionDialog(props) {
  const [dialogState, setDialogState] = React.useState("INITIAL");

  return (
    <>
      {dialogState === "INITIAL" && (
        <Dialog
          title="Remove connection"
          confirmLabel="Remove"
          onConfirmClick={async () => {
            await props.onConfirm();
            setDialogState("SUCCESS");
          }}
          cancelLabel="Keep"
          onCancelClick={() => {
            props.onDialogClose();
          }}
        >
          {`Are you sure you want to remove connection${props.nameOfOtherUser ? ` with ${props.nameOfOtherUser}` : ""}?`}
        </Dialog>
      )}

      {dialogState === "SUCCESS" && (
        <Dialog
          title="Connection removed"
          confirmLabel="OK"
          onConfirmClick={async () => {
            props.onDialogClose();
          }}
        >
          {`Your connection ${props.nameOfOtherUser ? `with ${props.nameOfOtherUser}` : ""} has been removed.`}
        </Dialog>
      )}
    </>
  );
}

const S = {};

S.Buttons = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  & > div {
    margin: 0 5px;
  }
`;
